/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * `/registry?tab=catalog`: the built-in catalogue of every TLD in the world (IANA root zone and
 * registrable second-level zones from the Public Suffix List). Staff enable a zone ("Bật TLD":
 * choose the registry, optionally copy another TLD's settings and prices) or suspend it ("Tắt
 * TLD"). "Thêm TLD" sets a zone up to sell in one step (connection, costs, price, add-ons).
 * Zones enabled outside the catalogue are listed below it with "Ngừng bán" (same endpoint).
 */

import { Power, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { TldCatalogEntry, TldCatalogPage as CatalogResponse, TldClass } from '../../api/areas/registry';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { formatCount } from '../../lib/format';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { TldSetupButton } from './TldSetupWizard';
import { enumLabel, FilterSelect, RefreshButton, ResetButton, useParamSetter, useRegistryCrumbs } from './shared';

const CLASSES: TldClass[] = ['VN', 'GTLD', 'NEW_GTLD', 'CCTLD', 'OTHER'];
const PAGE = 100;

function EnableForm({ close, changed, entry }: DialogBodyProps & { entry: TldCatalogEntry }) {
  const { t } = useTranslation('registry');
  const registries = useApi((signal) => api.listRegistries({ signal }), []);
  const enabled = useApi((signal) => api.listTlds({ status: 'ACTIVE', limit: 500 }, { signal }), []);
  const [registry, setRegistry] = useState(entry.tld?.registry_id ?? '');
  const [copyFrom, setCopyFrom] = useState('');
  const [copyPrices, setCopyPrices] = useState(false);
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) =>
    api.enableTld(entry.zone, { registry_id: registry, copy_from: copyFrom || undefined, copy_prices: copyFrom ? copyPrices : false, reason: reason.trim() }, opts),
  );
  const result = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {result ? (
        <Callout tone="ok">
          {result.created ? t('catalog.enabled', { zone: result.zone }) : result.reactivated ? t('catalog.reactivated', { zone: result.zone }) : t('catalog.already', { zone: result.zone })}{' '}
          {t('catalog.next')}
        </Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t('catalog.enableIntro', { cls: enumLabel('tldClass', entry.tld_class), dossier: enumLabel('dossierType', entry.dossier_type) })}
          </p>
          <Field label={t('catalog.registry')} hint={t('catalog.registryHint')}>
            <select className={selectClass} value={registry} onChange={(e) => setRegistry(e.target.value)} required>
              <option value="">{t('catalog.choose')}</option>
              {(registries.status === 'success' ? registries.data.items : []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('catalog.copyFrom')} hint={t('catalog.copyFromHint')}>
            <select className={selectClass} value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">{t('catalog.classDefaults', { cls: enumLabel('tldClass', entry.tld_class) })}</option>
              {(enabled.status === 'success' ? enabled.data.items : [])
                .filter((x) => x.zone !== entry.zone)
                .map((x) => (
                  <option key={x.id} value={x.zone}>
                    .{x.zone}
                  </option>
                ))}
            </select>
          </Field>
          {copyFrom ? (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={copyPrices} onChange={(e) => setCopyPrices(e.target.checked)} />
              {t('catalog.copyPrices')}
            </label>
          ) : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!registry || !reason.trim()} submit={result ? undefined : t('catalog.enable')} onClose={close} />
    </form>
  );
}

export function TldCatalogPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('catalog.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const q = params.get('q') ?? '';
  const cls = (params.get('class') ?? '') as TldClass | '';
  const state = (params.get('state') ?? '') as 'enabled' | 'disabled' | '';
  const offset = Number(params.get('offset') ?? '0') || 0;
  const [draft, setDraft] = useState(q);
  const result = useApi(
    (signal) => api.listTldCatalog({ q: q || undefined, class: cls || undefined, state: state || undefined, limit: PAGE, offset }, { signal }),
    [q, cls, state, offset],
  );
  // How many zones are sold now, whatever the filter (one row asked, the total read).
  const selling = useApi((signal) => api.listTldCatalog({ state: 'enabled', limit: 1, offset: 0 }, { signal }), [result.data]);
  const reload = result.reload;
  const columns: Column<TldCatalogEntry>[] = [
    {
      key: 'zone',
      header: t('catalog.zone'),
      cell: (e) => (
        <span className="grid">
          <span className="font-medium">.{e.unicode}</span>
          {e.unicode !== e.zone ? <span className="font-mono text-xs text-muted-foreground">{e.zone}</span> : null}
        </span>
      ),
    },
    { key: 'class', header: t('catalog.class'), cell: (e) => enumLabel('tldClass', e.tld_class) },
    { key: 'type', header: t('catalog.ianaType'), cell: (e) => <span className="text-xs">{e.iana_type ?? (e.parent ? t('catalog.secondLevel', { parent: e.parent }) : '—')}</span> },
    {
      key: 'lookup',
      header: t('catalog.lookup'),
      cell: (e) => (
        <span className="text-xs">
          {e.rdap_base_urls.length ? 'RDAP' : ''}
          {e.rdap_base_urls.length && e.whois_server ? ' · ' : ''}
          {e.whois_server ?? ''}
          {!e.rdap_base_urls.length && !e.whois_server ? '—' : ''}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('catalog.state'),
      cell: (e) =>
        e.selling ? (
          <Badge tone="ok">{t('catalog.selling', { registry: e.tld?.registry_code ?? '' })}</Badge>
        ) : e.tld ? (
          <Badge tone="warn">{t('catalog.suspended')}</Badge>
        ) : (
          <Badge tone="neutral">{t('catalog.off')}</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      cell: (e) =>
        e.selling ? (
          <ReasonAction
            label={t('catalog.disable')}
            icon={PowerOff}
            variant="ghost"
            danger
            title={t('catalog.disableTitle', { zone: e.unicode })}
            warning={t('catalog.disableHint')}
            run={(reason, opts) => api.disableTld(e.zone, { reason }, opts)}
            done={() => t('catalog.disabled')}
            onChanged={reload}
          />
        ) : (
          <ActionDialog label={t('catalog.enable')} icon={Power} title={t('catalog.enableTitle', { zone: e.unicode })} onChanged={reload}>
            {(p) => <EnableForm {...p} entry={e} />}
          </ActionDialog>
        ),
    },
  ];
  // Zones enabled outside the catalogue (e.g. `.example`, `.test` from demo data): they cannot be
  // enabled again here, only taken off sale.
  const outsideColumns: Column<CatalogResponse['enabled_outside_catalog'][number]>[] = [
    { key: 'zone', header: t('catalog.zone'), cell: (o) => <span className="font-medium">.{o.zone}</span> },
    {
      key: 'state',
      header: t('catalog.state'),
      cell: (o) => (o.status === 'ACTIVE' ? <Badge tone="ok">{t('catalog.selling', { registry: o.registry_code })}</Badge> : <Badge tone="warn">{t('catalog.suspended')}</Badge>),
    },
    {
      key: 'actions',
      header: '',
      cell: (o) =>
        o.status === 'ACTIVE' ? (
          <ReasonAction
            label={t('catalog.stopSelling')}
            icon={PowerOff}
            variant="ghost"
            danger
            title={t('catalog.stopSellingTitle', { zone: o.zone })}
            warning={t('catalog.disableHint')}
            run={(reason, opts) => api.disableTld(o.zone, { reason }, opts)}
            done={() => t('catalog.stopped')}
            onChanged={reload}
          />
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title={t('catalog.title')}
        crumbs={crumbs}
        subtitle={t('catalog.subtitle')}
        actions={
          <>
            <TldSetupButton onChanged={reload} />
            <RefreshButton onClick={reload} />
          </>
        }
      />
      <StatGrid className="mb-5 xl:grid-cols-3">
        <StatCard label={t('hub.catalog.size')} value={result.status === 'success' ? formatCount(result.data.catalog_size) : '…'} hint={result.status === 'success' ? t('hub.catalog.sizeHint', { version: result.data.catalog_version }) : undefined} />
        <StatCard label={t('hub.catalog.selling')} value={selling.status === 'success' ? formatCount(selling.data.total) : '…'} tone="ok" hint={t('hub.catalog.sellingHint')} to="/products?tab=tlds" linkLabel={t('hub.catalog.sellingLink')} />
        <StatCard label={t('hub.catalog.matches')} value={result.status === 'success' ? formatCount(result.data.total) : '…'} hint={t('hub.catalog.matchesHint')} />
      </StatGrid>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          set('q', draft.trim());
        }}
      >
        <FilterBar actions={<ResetButton onClick={() => (setDraft(''), reset())} disabled={!q && !cls && !state} />}>
          <Field label={t('catalog.search')} className="w-full sm:w-64">
            <Input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="vn, com.vn, рф…" />
          </Field>
          <FilterSelect label={t('catalog.class')} value={cls} onChange={(v) => set('class', v)} options={CLASSES.map((c) => ({ value: c, label: enumLabel('tldClass', c) }))} />
          <FilterSelect
            label={t('catalog.state')}
            value={state}
            onChange={(v) => set('state', v)}
            options={[
              { value: 'enabled', label: t('catalog.filterEnabled') },
              { value: 'disabled', label: t('catalog.filterDisabled') },
            ]}
          />
        </FilterBar>
      </form>
      <Section flush title={result.status === 'success' ? t('catalog.count', { total: result.data.total, size: result.data.catalog_size, version: result.data.catalog_version }) : t('catalog.title')}>
        {result.status === 'loading' ? <Loading rows={6} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={reload} /> : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={result.data.items} rowKey={(e) => e.zone} empty={<EmptyState title={t('catalog.empty')} />} />
            <div className="flex items-center justify-between gap-2 border-t px-4 py-3 text-xs text-muted-foreground">
              <span>{result.data.attribution ?? ''}</span>
              <span className="flex gap-2">
                {offset > 0 ? (
                  <button type="button" className="text-primary hover:underline" onClick={() => set('offset', String(Math.max(0, offset - PAGE)))}>
                    {t('catalog.previous')}
                  </button>
                ) : null}
                {offset + PAGE < result.data.total ? (
                  <button type="button" className="text-primary hover:underline" onClick={() => set('offset', String(offset + PAGE))}>
                    {t('catalog.nextPage')}
                  </button>
                ) : null}
              </span>
            </div>
          </>
        ) : null}
      </Section>
      {result.status === 'success' && result.data.enabled_outside_catalog.length ? (
        <Section flush className="mt-5" title={t('catalog.outsideTitle')} description={t('catalog.outsideHint')}>
          <DataTable columns={outsideColumns} rows={result.data.enabled_outside_catalog} rowKey={(o) => o.zone} />
        </Section>
      ) : null}
    </>
  );
}
